import { signInWithCustomToken } from 'firebase/auth';
import { projectAuth } from '@/firebase/config';
import { auth0 } from './auth0';
import { FUNCTIONS_ORIGIN } from '@/utils/functionsOrigin';

// Layer 2 (READ path) — establish a Firebase Auth session from the Auth0 login.
//
// Firestore security rules only understand Firebase Auth (`request.auth`). Auth0
// is our IdP, so without this bridge every client Firestore read is
// unauthenticated and the rules can't enforce tenant isolation. ensureFirebaseAuth
// exchanges the caller's Auth0 access token for a Firebase custom token (minted by
// the `mintFirebaseToken` Cloud Function with uid === Auth0 sub) and signs in.
//
// MUST be awaited before the first Firestore read. The router guard (combinedGuard)
// awaits it up front, which covers the guard's own getDocs() calls and every
// component/composable that mounts afterwards.
//
// The Authorization header for the mint request is attached automatically by the
// functionAuthInterceptor (mintFirebaseToken is on its allowlist), so a plain
// fetch is all we need here.

const MINT_URL = `${FUNCTIONS_ORIGIN}/mintFirebaseToken`;

// De-dupes concurrent callers (e.g. several route changes firing at once) onto a
// single in-flight mint+sign-in instead of racing multiple token mints.
let inflight = null;

// Firebase restores any persisted session asynchronously; currentUser is null
// until that first resolution. Wait for it once so we don't mint a fresh token on
// every reload when a valid session is about to come back.
function waitForAuthReady() {
  // authStateReady() exists in firebase >= 9.17.
  if (typeof projectAuth.authStateReady === 'function') {
    return projectAuth.authStateReady();
  }
  return new Promise((resolve) => {
    const unsub = projectAuth.onAuthStateChanged(() => {
      unsub();
      resolve();
    });
  });
}

export async function ensureFirebaseAuth() {
  const sub = auth0.user?.value?.sub;
  // Not (yet) logged into Auth0 — nothing to bridge. The guard handles the
  // unauthenticated case separately by redirecting to login.
  if (!sub) return;

  await waitForAuthReady();

  // Custom-token uid === Auth0 sub, so a restored session for the same user is
  // already correct and re-usable. The SDK auto-refreshes its ID token, so we
  // only ever mint once per user session.
  if (projectAuth.currentUser && projectAuth.currentUser.uid === sub) {
    return;
  }

  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // No body needed — the backend proves membership from the verified Auth0
      // token's sub and mints a session valid across all the user's orgs.
      const res = await fetch(MINT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        // P2 (2026-08-24, retired-staff authz Phase 1) — the STATUS matters to
        // the caller, so it travels with the error.
        //
        // Dev-test defect 1 (2026-08-24) added the `code` alongside it. A 403
        // has two causes — "retired everywhere" and "never a member of any
        // org" — and only mintFirebaseToken can tell them apart. It already
        // returns `code: "RETIRED"` for the first, but nothing read the body, so
        // the distinction was thrown away here and both landed on the same
        // generic screen. The parse is defensive: a 403 from an infrastructure
        // layer (a proxy, IAM) has no JSON body at all, and that must degrade to
        // the generic message rather than throwing a SyntaxError that masks the
        // real status.
        //
        // mintFirebaseToken returns 403 for exactly one class of caller: someone
        // who is not an active member of any organisation (never was, or is now
        // retired everywhere). That is TERMINAL — retrying, re-authenticating
        // with Auth0, or bouncing to login all fail identically forever, because
        // Auth0 knows nothing about retirement and will keep authenticating them
        // happily. Without this, combinedGuard treats every mint failure alike
        // and sends them to login, where they log in successfully and land back
        // here: an infinite bounce with no explanation.
        //
        // Any other status (500, network, secret rotation) is transient and must
        // NOT be conflated with it — see combinedGuard's use of `.status`.
        let code = null;
        try {
          code = (await res.json())?.code || null;
        } catch (parseError) {
          // No body, or not JSON. Status alone still routes correctly.
        }
        const err = new Error(`mintFirebaseToken returned ${res.status}`);
        err.status = res.status;
        err.code = code;
        throw err;
      }
      const { token } = await res.json();
      if (!token) {
        throw new Error('mintFirebaseToken returned no token');
      }
      await signInWithCustomToken(projectAuth, token);
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
